import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { OpenService } from '@patsnap/tahiti-core/services/open-service';
import { isUserLogin } from '@eureka/utils/account';
import { loginSDK } from '@eureka/login-sdk';
import type { AuthService } from '../auth-service';
import { ShareAccessCheckRepository, ShareOwnerInfoRepository } from '../share-tools-repository';
import type { AuthModuleData, AuthPageParams, ShareAuthCheckData } from '../../types';
import { AuthModel } from '../../models/auth.model';
import { ejectAirShareInterceptor, injectAirShareInterceptor } from '@eureka/services/src/apis/interceptors/share.interceptor';
import { Site, SLD } from '@patsnap-utils/biz';

@injectable()
export class AuthServiceImpl implements AuthService {
    private _pageParams: AuthPageParams = {
        currentPageType: '',
        currentDetailId: '',
    }

    constructor(
        @inject(ShareAccessCheckRepository) private _shareAccessCheckRepository: ShareAccessCheckRepository,
        @inject(ShareOwnerInfoRepository) private _shareOwnerInfoRepository: ShareOwnerInfoRepository,
        @inject(AuthModel) private _authModel: AuthModel,
        @inject(OpenService) private _openService: OpenService
    ) {}

    async shareAccessCheck(data: ShareAuthCheckData) {
        try {
            const isShare = await this._shareAccessCheckRepository.shareAccessCheck(data)

            if (isShare) {
                injectAirShareInterceptor();
                this.updateAuthState({ isShare });
                this.getOwnerInfo(data);
            } else {
                ejectAirShareInterceptor();
                this.updateAuthState({ isShare: false, ownerName: '' });
            }

            return isShare;
        } catch (err) {
            ejectAirShareInterceptor();
            this.updateAuthState({ isShare: false, ownerName: '' });
            this.handleAccessError();
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject('');
        }
    }

    async getOwnerInfo(data: ShareAuthCheckData) {
        try {
            const response = await this._shareOwnerInfoRepository.getShareOwnerInfo(data);
            this.updateAuthState({ ownerName: response.ownerName, ownerId: response.ownerId })
            return response;
        } catch (err) {
            this.updateAuthState({ ownerName: '', ownerId: '' })
            return { ownerName: '', ownerId: '' }
        }
    }

    updateAuthState(state: Partial<AuthModuleData>) {
        this._authModel.updateModuleData(v => ({
            ...v,
            ...state,
        }))
    }

    get pageParams() {
        return this._pageParams;
    }

    get authModelData() {
        return this._authModel.modelData;
    }

    setPageParams(params: Partial<typeof this._pageParams>) {
        this._pageParams = {
            ...this._pageParams,
            ...params,
        }
    }

    backToHome() {
        const url = new Site({ sld: SLD.EUREKA, pathname: 'air' }).href;
        this._openService.open(url, {
            target: '_self',
        });
    }

    handleLogin() {
        return isUserLogin().then(isLogin => {
            this.updateAuthState({ isLogin })
            return isLogin;
        })
    }

    handleAccessError() {
        this.handleLogin().then(isLogin => {
            if (!isLogin) {
                loginSDK.open({
                    withoutClose: true,
                }).then(() => {
                    this.backToHome();
                });
            } else {
                this.backToHome();
            }
        })
    }
}
